import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type ConditionalFieldParams,
  type ElementSizingMode,
  type IconSelectOption,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW_DIALOG,
  type PropertiesConfig,
  type PropertiesConfigInputOptionsToggle,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { CalendarDay } from '../../components/calendar-dates-view';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { CalendarDefinitionMapper } from './ai/calendar.definition-mapper';
import { CalendarElementHandler } from './ai/calendar.element-handler';
import { CalendarComponent } from './calendar/calendar.component';
import {
  CALENDAR_DEFAULTS,
  type CalendarDayViewColumnWidthMode,
  type CalendarPropsModel,
} from './calendar-element.models';
import {
  CalendarDateFormat,
  CalendarTimeFormat,
  CalendarTimeScale,
  CalendarView,
} from './calendar.models';
import { CalendarPropsBuilder } from './calendar.props-builder';
import { CalendarPropsExtractor } from './calendar.props-extractor';
import { calendarV2Migration } from './migrations/002-calendar.migration';
import { calendarV3Migration } from './migrations/003-calendar.migration';
import { calendarConfig } from './providers/config';

export type CalendarDef = WithProps<CalendarPropsModel> &
  WithIoConfig<'value' | 'visible', 'action' | 'query'>;
export type CalendarElement = CanvasElementFrom<'calendar'>;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    calendar: CalendarDef;
  }
}

const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'calendar',
  componentType: CalendarComponent,
  isListed: true,
  name: 'Calendar',
  width: { mode: 'full', value: 715, fixed: true },
  height: { mode: 'value', value: 670, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: CalendarPropsBuilder,
  propsExtractor: CalendarPropsExtractor,
  defaults: getDefaults(),
  initial: getInitialValues(),
  groups: ['advanced'],
  toolbar: ['triggerClick'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  styleExcludePaths: ['style.dateSelector.currentState'],
  features: {
    loadingIndication: 'fadeWithIndicator',
    visualStyle: 'block',
  },
  ai: {
    name: 'calendar',
    handler: () => CalendarElementHandler,
    definitionMapper: () => CalendarDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: calendarV2Migration,
    3: calendarV3Migration,
  },
});

function getIoConfig(): IoConfig<'value' | 'visible', 'action' | 'query'> {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
        isQueryable: true,
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'action', schema: DEFAULT_SCHEMA },
      { eventType: 'query', schema: DEFAULT_SCHEMA }, // pagination event doesn't require a schema
    ],
  };
}

function getDefaults(): PropsOf<'calendar'> {
  return CALENDAR_DEFAULTS as PropsOf<'calendar'>;
}

function getInitialValues(): PartialPropsOf<'calendar'> {
  return {
    version: 2,
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('calendar', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Source',
        [],
        [
          epc.fields.selectDataSourceField(
            { key: 'value', label: 'Source', sources: 'list', bindings: [] },
            'normal',
            'Events',
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'content.input.titleColumn',
                '* Title',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.startColumn',
                '* Start time',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('DateTime'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.endColumn',
                '* End time',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('DateTime'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.calendarColumn',
                'Calendar',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  allowNone: true,
                  optionsFilter: epc.utils.columnFiltersCombination(
                    epc.utils.columnFormatFilter('!secureText'),
                    epc.utils.columnMultipleFilter(),
                  ),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.timeZoneColumn',
                'Time zone',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  allowNone: true,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
            ],
            'data.?key:value.dataSource',
            (value) => !value,
          ),
          epc.utils.separator('Calendar column size', { useNormalFontWeight: true }),
          epc.fields.radiosField(
            'content.dayViewOptions.widthMode',
            '',
            [
              {
                value: 'fit',
                label: 'Fit',
                description: 'Fit all calendars within the container',
              },
              {
                value: 'count',
                label: 'Count',
                description: 'Set the number of visible calendars',
              },
              {
                value: 'fixed',
                label: 'Set width',
                description: 'Set a custom width for calendars',
              },
            ] satisfies IconSelectOption<CalendarDayViewColumnWidthMode>[],
            { hideLabel: true },
          ),
          epc.utils.conditionalField(
            epc.fields.sliderField('content.dayViewOptions.widthValue', 'Width', {
              hideSlider: true,
              units: [
                { min: calendarConfig.minDayViewColumnWidthPx, max: 9999, step: 1, unit: 'px' },
              ],
            }),
            'content.dayViewOptions.widthMode',
            (mode: CalendarDayViewColumnWidthMode) => mode !== 'fixed',
          ),
          epc.utils.conditionalField(
            epc.fields.textInput('content.dayViewOptions.showCalendarsNumber', 'Calendars', {
              inputType: 'number',
              numberMin: 1,
              numberMax: 25,
              checkMin: true,
              checkMax: true,
            }),
            'content.dayViewOptions.widthMode',
            (mode: CalendarDayViewColumnWidthMode) => mode !== 'count',
          ),
        ],
        { enabledByDefault: true },
      ),
      epc.subsections.subsection('content.calendarOptions', 'Options', [
        epc.fields.selectFieldFromList(
          'content.calendarOptions.view',
          'View',
          [
            { label: 'Day', value: CalendarView.Day },
            { label: '3 Day', value: CalendarView.ThreeDay },
            { label: 'Week', value: CalendarView.Week },
            { label: 'Month', value: CalendarView.Month },
          ],
          { allowNone: false },
        ),
        epc.fields.selectFieldFromList(
          'content.calendarOptions.startWeekOn',
          'Start week on',
          [
            { label: 'Monday', value: CalendarDay.Monday },
            { label: 'Saturday', value: CalendarDay.Saturday },
            { label: 'Sunday', value: CalendarDay.Sunday },
          ],
          { allowNone: false },
        ),
        epc.fields.radiosField('content.calendarOptions.timeFormat', 'Time format', [
          { label: '12H', value: CalendarTimeFormat['12H'] },
          { label: '24H', value: CalendarTimeFormat['24H'] },
        ]),
        epc.fields.radiosField('content.calendarOptions.timeScale', 'Time scale', [
          { label: '1H', value: CalendarTimeScale['1H'] },
          { label: '30MIN', value: CalendarTimeScale['30MIN'] },
        ]),
        epc.fields.selectFieldFromList(
          'content.calendarOptions.dateFormat',
          'Date format',
          [
            {
              label: '08/25/2023',
              value: CalendarDateFormat.ShortDateUS,
            },
            {
              label: '25/08/2023',
              value: CalendarDateFormat.ShortDateEU,
            },
            {
              label: 'Aug 25, 2023',
              value: CalendarDateFormat.MediumDate,
            },
            {
              label: 'August 25, 2023',
              value: CalendarDateFormat.LongDate,
            },
            {
              label: 'Friday Aug 25, 2023',
              value: CalendarDateFormat.FullDate,
            },
          ],
          { allowNone: false },
        ),
        epc.fields.selectFieldFromList(
          'content.calendarOptions.defaultDurationMinutes',
          'Default duration',
          [
            {
              label: '15 minutes',
              value: 15,
            },
            {
              label: '30 minutes',
              value: 30,
            },
            {
              label: '45 minutes',
              value: 45,
            },
            {
              label: '60 minutes',
              value: 60,
            },
            {
              label: '90 minutes',
              value: 90,
            },
            {
              label: '120 minutes',
              value: 120,
            },
            {
              label: '150 minutes',
              value: 150,
            },
            {
              label: '180 minutes',
              value: 180,
            },
          ],
          { allowNone: false },
        ),
        epc.fields.timeRangeField('content.time', 'Working hours', {
          timeFormatPath: 'content.calendarOptions.timeFormat',
        }),
        epc.fields.textInput('content.calendarsLabel', 'Calendars label'),
        epc.utils.separatedField(
          epc.fields.toggleField('content.calendarOptions.hideWeekends', 'Hide weekends', {
            renderType: 'check',
          }),
        ),
        epc.fields.toggleField(
          'content.calendarOptions.disableEventDrag',
          'Disable events drag & drop',
          { renderType: 'check' },
        ),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.calendarOptions.hideCalendarName', 'Hide calendar name', {
            renderType: 'check',
          }),
          'content.input.calendarColumn',
          (targetPathValue: string, _model: any, params: ConditionalFieldParams) => {
            return (
              !targetPathValue?.length ||
              (params.dataFeatures?.elementType === 'calendar' &&
                params.dataFeatures?.calendarsCount !== 1)
            );
          },
        ),
      ]),
      epc.subsections.subsection('content', 'Actions', [
        ...epc.utils.disabledFields(
          [
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemAdd.enabled', 'Add new appointment'),
              {
                action: () => ({ id: ITEMS_CONTAINER_ACTION_ADD }),
                conditionsPath: 'content.itemAdd.visibilityConditions',
                conditionsExpressionPath: 'content.itemAdd.visibilityExpression',
                conditionsConfig: {
                  title: 'Add new appointment visibility',
                  scope: 'component',
                },
              },
            ),
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemEdit.enabled', 'Edit appointment'),
              {
                action: () => ({ id: ITEMS_CONTAINER_ACTION_EDIT }),
                conditionsPath: 'content.itemEdit.visibilityConditions',
                conditionsExpressionPath: 'content.itemEdit.visibilityExpression',
                conditionsConfig: {
                  title: 'Edit appointment visibility',
                  scope: 'component',
                },
              },
            ),
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemDelete.enabled', 'Delete appointment'),
              {
                workflowId: (model) =>
                  getValueByPath(model, 'content.itemDelete.targetId') ?? undefined,
                conditionsPath: 'content.itemDelete.visibilityConditions',
                conditionsExpressionPath: 'content.itemDelete.visibilityExpression',
                conditionsConfig: {
                  title: 'Delete appointment visibility',
                  scope: 'component',
                },
              },
            ),
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemViewDialog.enabled', 'View appointment'),
              {
                action: () => ({ id: ITEMS_CONTAINER_ACTION_VIEW_DIALOG }),
                conditionsPath: 'content.itemView.visibilityConditions',
                conditionsExpressionPath: 'content.itemView.visibilityExpression',
                conditionsConfig: {
                  title: 'View appointment visibility',
                  scope: 'component',
                },
              },
            ),
          ],
          (model: unknown) =>
            !getValueByPath(model, 'data.?key:value.dataSource')
              ? 'The value of the element is not found'
              : undefined,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.subsection('content.calendar', 'Size', [
        epc.root.elementWidthField({ min: 660, max: 1000, unit: 'px', step: 1 }, WIDTH_OPTIONS),
        epc.root.elementHeightField({ min: 500, max: 1000, unit: 'px', step: 1 }, HEIGHT_OPTIONS),
      ]),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.gridBackground', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.headerBackground', 'Header background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.lines', 'Grid line', { allowGradient: false }),
          epc.fields.colorField('style.general.secondaryLines', 'Secondary grid line', {
            allowGradient: false,
          }),
        ]),
        epc.subsections.calendarDaysStyleSubsection('style.dateSelector', 'Date Selector'),
        epc.subsections.subsection('style.appointments', 'Appointments', [
          epc.fields.textInfo('style.appointments', 'Label', {
            fontSize: 13,
            classes: ['font-medium'],
          }),
          epc.fields.colorSimpleField('style.appointments.palette.foreground', 'Color'),
          epc.fields.sliderField(`style.appointments.fontSize`, 'Size', [
            { min: 8, max: 72, unit: 'px' },
          ]),
          epc.utils.separator(),
          epc.fields.tagsColorPaletteField('style.appointments.palette', 'Container', {
            withoutForeground: true,
            hideForeColor: true,
            pickPaletteForeground: true,
            boldLabel: true,
          }),
          epc.fields.colorField('style.appointments.outline', 'Outline', { allowGradient: false }),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.titles',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
